import { Grid } from '@mui/material';
import { isEmail, PhoneNumberField, StateSelect } from '@side/pantry';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { isValueEqualToCountryPrefix } from '@/common/utils/phoneNumber';
import { StreetAutocomplete } from '@/components/AddressInput/StreetAutocomplete/StreetAutocomplete';
import { FormTextField } from '@/components/Inputs/FormTextField';
import {
  LICENSE_NUMBER_ERROR_MESSAGE,
  LICENSE_NUMBER_PATTERN,
} from '@/constants';
import type { IsFieldInvalidCallback } from '@/views/Referrals/components/AgentDetails/SecondAgentDetails/ContactFormModal.helpers';
import type { AgentFields } from '@/views/Referrals/views/NewReferral/types';
import {
  TrackContactModalEvent,
  useTrackContactModalAnalytics,
} from '$analytics/useTrackAnalytics/domains/contactModal';

interface Props {
  isFieldInvalid: IsFieldInvalidCallback;
}

export function BrokerageDetails({ isFieldInvalid }: Props) {
  const trackEvent = useTrackContactModalAnalytics();

  const { control, setValue } = useFormContext<AgentFields>();

  const phoneFieldName = 'supervisingBroker.cellPhone';
  const [phone, street1] = useWatch<
    AgentFields,
    [typeof phoneFieldName, 'brokerage.street1']
  >({
    name: [phoneFieldName, 'brokerage.street1'],
  });

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    trackEvent(TrackContactModalEvent.FieldUpdate, {
      eventTarget: e.target.name,
      value: e.target.value,
    });
  };

  return (
    <>
      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          fixedHeight
          name="supervisingBroker.firstName"
          label="Managing Broker First Name"
          required
          onBlur={handleBlur}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          fixedHeight
          name="supervisingBroker.lastName"
          label="Managing Broker Last Name"
          required
          onBlur={handleBlur}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          fixedHeight
          name="supervisingBroker.email"
          rules={{
            validate: (value: string) => isEmail(value) || 'Invalid email',
          }}
          label="Managing Broker Email"
          required
          onBlur={handleBlur}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <PhoneNumberField
          fixedHeight
          fullWidth
          id={phoneFieldName}
          label="Managing Broker Cell Phone"
          name={phoneFieldName}
          onChange={(value) => {
            if (isValueEqualToCountryPrefix(value)) {
              setValue(phoneFieldName, '');
              return;
            }

            setValue(phoneFieldName, value);
          }}
          value={phone}
          onBlur={(newValue) => {
            trackEvent(TrackContactModalEvent.FieldUpdate, {
              eventTarget: 'brokerage.phone',
              value: newValue,
            });
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          fixedHeight
          name="brokerage.licenseNumber"
          label="Brokerage License Number"
          required
          rules={{
            pattern: {
              value: LICENSE_NUMBER_PATTERN,
              message: LICENSE_NUMBER_ERROR_MESSAGE,
            },
          }}
          error={isFieldInvalid('brokerage.licenseNumber')}
          onBlur={handleBlur}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          fixedHeight
          name="brokerage.taxId"
          label="Tax ID"
          onBlur={handleBlur}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 7.5 }}>
        <StreetAutocomplete
          handleAutocompleteSelect={(address) => {
            setValue('brokerage.city', address?.city ?? '', {
              shouldValidate: true,
            });
            setValue('brokerage.state', address?.state ?? '', {
              shouldValidate: true,
            });
            setValue('brokerage.street1', address?.street1 ?? '', {
              shouldValidate: true,
            });
            setValue('brokerage.zip', address?.zip ?? '', {
              shouldValidate: true,
            });

            trackEvent(TrackContactModalEvent.FieldUpdate, {
              eventTarget: 'brokerage.city',
              value: address?.city,
            });
            trackEvent(TrackContactModalEvent.FieldUpdate, {
              eventTarget: 'brokerage.state',
              value: address?.state,
            });
            trackEvent(TrackContactModalEvent.FieldUpdate, {
              eventTarget: 'brokerage.street1',
              value: address?.street1,
            });
            trackEvent(TrackContactModalEvent.FieldUpdate, {
              eventTarget: 'brokerage.zip',
              value: address?.zip,
            });
          }}
          highlight={!street1 ? 'secondary' : undefined}
          label="Street"
          name="brokerage.street1"
          required
          onInputBlur={handleBlur}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4.5 }}>
        <FormTextField
          fixedHeight
          name="brokerage.street2"
          label="Apt/Unit/Suite Number"
          onBlur={handleBlur}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FormTextField
          fixedHeight
          name="brokerage.city"
          label="City"
          required
          onBlur={handleBlur}
        />
      </Grid>

      <Grid size={{ xs: 6, md: 3 }}>
        <Controller
          name="brokerage.state"
          control={control}
          rules={{
            required: true,
          }}
          render={({
            field: { value, onChange: _onChange, ...fieldProps },
            fieldState: { invalid, error },
          }) => (
            <StateSelect
              abbreviations
              AutocompleteProps={{
                onChange: (_, newValue) => {
                  setValue(
                    'brokerage.state',
                    typeof newValue === 'string' ? newValue : '',
                  );

                  trackEvent(TrackContactModalEvent.FieldUpdate, {
                    eventTarget: 'brokerage.state',
                    value: newValue,
                  });
                },
                value,
              }}
              TextFieldProps={{
                error: invalid,
                fixedHeight: true,
                fullWidth: true,
                highlight: !value ? 'secondary' : undefined,
                helperText: error?.message,
                label: 'State',
                required: true,
                ...fieldProps,
              }}
            />
          )}
        />
      </Grid>

      <Grid size={{ xs: 6, md: 3 }}>
        <FormTextField
          fixedHeight
          name="brokerage.zip"
          label="Zip"
          required
          onBlur={handleBlur}
        />
      </Grid>
    </>
  );
}
